import { ArrowLeft, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import Select from 'react-select'
import api from '../../../../config/AxiosInstance'
import '../../../../styles/style.css'
import toast, { Toaster } from 'react-hot-toast'

const cardStyle = {
  backgroundColor: '#fff',
  borderRadius: '8px',
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  padding: '1.5rem',
  marginBottom: '1.5rem'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const headerTitleStyle = {
  color: '#343a40',
  fontSize: '24px',
  fontWeight: '700'
}

function EditManageRolePage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [optionRoles, setOptionRoles] = useState([])
  const [inputRole, setInputRole] = useState('')
  const [selectedRole, setSelectedRole] = useState(null)

  const [selectedUsername, setSelectedUsername] = useState(null)

  const [selectedBranches, setSelectedBranches] = useState([])
  const [selectedWarehouses, setSelectedWarehouses] = useState([])

  const [showModalBranch, setShowModalBranch] = useState(false)
  const [showModalWarehouse, setShowModalWarehouse] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true)
        const res = await api.get(`/cms/manage-role/${id}`)
        const data = res.data.data

        setSelectedUsername({
          value: data.user_id,
          label: data.username
        })

        setSelectedRole({
          value: data.role_id,
          label: data.role_name
        })

        if (data.locations) {
          setSelectedBranches(data.locations)
        }

        if (data.warehouse) {
          setSelectedWarehouses(data.warehouse)
        }
      } catch (err) {
        const errMsg =
          err?.response?.data?.errors?.message || 'Failed to load data'
        toast.error(errMsg, {
          position: 'top-right',
          style: {
            fontSize: '14px',
            fontWeight: '600'
          }
        })
        navigate('/manage/role')
      } finally {
        setIsLoadingData(false)
      }
    }

    if (id) {
      fetchData()
    }
  }, [id, navigate])

  useEffect(() => {
    const controller = new AbortController()
    const fetchRoles = async () => {
      try {
        const res = await api.get(`/cms/role/list/all?role_name=${inputRole}`, {
          signal: controller.signal
        })
        const optionValue = []
        if (res.data.data.length > 0) {
          res.data.data.map((role) =>
            optionValue.push({
              value: role.id,
              label: role.role_name
            })
          )
        }
        setOptionRoles(optionValue)
      } catch (err) {
        if (err.message !== 'canceled') {
          console.error(err)
        }
      }
    }
    fetchRoles()
    return () => controller.abort()
  }, [inputRole])

  const handleRemoveBranch = (branchId) => {
    const newBranches = selectedBranches.filter((b) => b.id !== branchId)
    setSelectedBranches(newBranches)
    setSelectedWarehouses((prev) =>
      prev.filter((w) => w.location_id !== branchId)
    )
  }

  const handleRemoveWarehouse = (warehouseId) => {
    const newWarehouses = selectedWarehouses.filter((w) => w.id !== warehouseId)
    setSelectedWarehouses(newWarehouses)
  }

  const handleSubmit = async () => {
    if (!selectedUsername || !selectedRole) {
      toast.error('Please select Role', {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }

    setIsLoadingSubmit(true)
    try {
      const payload = {
        user_id: selectedUsername.value,
        role_id: selectedRole.value,
        location_ids: selectedBranches.map((b) => b.id),
        warehouse_ids: selectedWarehouses.map((w) => w.id)
      }

      await api.post('cms/manage-role/assign', payload)
      toast.success('Role Management Updated Successfully', {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      navigate('/manage/role')
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setIsLoadingSubmit(false)
    }
  }

  if (isLoadingData) {
    return <div className="p-5 text-center">Loading...</div>
  }

  return (
    <div className="container-fluid px-4 mb-5">
      <Row className="mb-4 align-items-center">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="light"
              className="btn-light rounded shadow-sm border p-2"
              style={{ width: '40px', height: '40px' }}
              onClick={() => setShowCancelModal(true)}
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h3 style={headerTitleStyle} className="mb-0">
                Edit Role Management
              </h3>
              <small className="text-muted fw-bold">
                ROLE MANAGEMENT {'>'} EDIT
              </small>
            </div>
          </div>
        </Col>
      </Row>

      <style>{`
        .form-check-input:checked {
          background-color: #F89D1D !important;
          border-color: #F89D1D !important;
        }
      `}</style>

      <Card style={cardStyle}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>
                USERNAME <span className="text-danger">*</span>
              </Form.Label>
              <Select
                value={selectedUsername}
                isDisabled={true}
                placeholder="Select"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>
                ROLE <span className="text-danger">*</span>
              </Form.Label>
              <Select
                options={optionRoles}
                onInputChange={(val) => setInputRole(val)}
                onChange={setSelectedRole}
                value={selectedRole}
                placeholder="Select"
                isClearable
                isSearchable
              />
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Card style={cardStyle}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold text-uppercase text-muted mb-0">
            List Branch
          </h6>
          <Button
            className="btn-primary-dark"
            style={{ backgroundColor: '#203568', borderColor: '#203568' }}
            onClick={() => setShowModalBranch(true)}
          >
            Select Branch
          </Button>
        </div>

        <Table responsive className="align-middle">
          <thead className="bg-light">
            <tr>
              <th className="py-3 text-muted border-0">Nama Branch</th>
              <th className="py-3 text-muted border-0 text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {selectedBranches.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-center py-5">
                  <span className="text-muted">No Data Selected</span>
                </td>
              </tr>
            ) : (
              selectedBranches.map((branch) => (
                <tr key={branch.id}>
                  <td>{branch.area_name}</td>
                  <td className="text-end">
                    <Button
                      variant="link"
                      className="text-danger p-0"
                      onClick={() => handleRemoveBranch(branch.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      <Card style={cardStyle}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold text-uppercase text-muted mb-0">
            List Warehouse
          </h6>
          <Button
            className={
              selectedBranches.length > 0 ? 'btn-primary-dark' : 'btn-secondary'
            }
            disabled={selectedBranches.length === 0}
            onClick={() => setShowModalWarehouse(true)}
            style={
              selectedBranches.length > 0
                ? { backgroundColor: '#203568', borderColor: '#203568' }
                : {}
            }
          >
            Select Warehouse
          </Button>
        </div>

        <Table responsive className="align-middle">
          <thead className="bg-light">
            <tr>
              <th className="py-3 text-muted border-0">Nama Warehouse</th>
              <th className="py-3 text-muted border-0 text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {selectedWarehouses.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-center py-5">
                  <span className="text-muted">No Data Selected</span>
                </td>
              </tr>
            ) : (
              selectedWarehouses.map((wh) => (
                <tr key={wh.id}>
                  <td>{wh.warehouse_name}</td>
                  <td className="text-end">
                    <Button
                      variant="link"
                      className="text-danger p-0"
                      onClick={() => handleRemoveWarehouse(wh.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      <div className="d-flex justify-content-between">
        <Button
          variant="light"
          className="text-danger fw-bold bg-white border py-2"
          onClick={() => setShowCancelModal(true)}
        >
          Cancel
        </Button>
        <Button
          className="btn-secondary py-2"
          disabled={
            isLoadingSubmit ||
            selectedBranches.length === 0 ||
            selectedWarehouses.length === 0 ||
            !selectedUsername ||
            !selectedRole
          }
          onClick={handleSubmit}
          style={{
            backgroundColor:
              selectedUsername &&
              selectedRole &&
              selectedBranches.length > 0 &&
              selectedWarehouses.length > 0
                ? '#203568'
                : '#6c757d',
            borderColor:
              selectedUsername &&
              selectedRole &&
              selectedBranches.length > 0 &&
              selectedWarehouses.length > 0
                ? '#203568'
                : '#6c757d'
          }}
        >
          {isLoadingSubmit ? 'Loading...' : 'Submit'}
        </Button>
      </div>

      {/* MODALS */}
      <ModalSelectBranch
        show={showModalBranch}
        onHide={() => setShowModalBranch(false)}
        onSave={(branches) => {
          setSelectedBranches(branches)
          const branchIds = branches.map((b) => b.id)
          setSelectedWarehouses((prev) =>
            prev.filter((w) => branchIds.includes(w.location_id))
          )
          setShowModalBranch(false)
        }}
        selected={selectedBranches}
      />

      <ModalSelectWarehouse
        show={showModalWarehouse}
        onHide={() => setShowModalWarehouse(false)}
        onSave={(warehouses) => {
          setSelectedWarehouses(warehouses)
          setShowModalWarehouse(false)
        }}
        selected={selectedWarehouses}
        branchIds={selectedBranches.map((b) => b.id)}
      />

      <ModalConfirmCancel
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={() => navigate('/manage/role')}
      />

      <Toaster />
    </div>
  )
}

function ModalSelectBranch({ show, onHide, onSave, selected }) {
  const [branches, setBranches] = useState([])
  const [checked, setChecked] = useState([])

  useEffect(() => {
    if (show) {
      setChecked(selected)
    }
  }, [show, selected])

  useEffect(() => {
    if (!show) return
    const controller = new AbortController()
    const fetch = async () => {
      try {
        const res = await api.get('master/locations', {
          signal: controller.signal
        })
        setBranches(res.data.data)
      } catch (err) {
        console.error(err)
      }
    }
    fetch()
    return () => controller.abort()
  }, [show])

  const handleCheck = (branch) => {
    const exists = checked.find((b) => b.id === branch.id)
    if (exists) {
      setChecked(checked.filter((b) => b.id !== branch.id))
    } else {
      setChecked([...checked, branch])
    }
  }

  const isSelected = (id) => !!checked.find((b) => b.id === id)

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      contentClassName="border-0 rounded-4 shadow-lg"
      scrollable
    >
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="fw-bold fs-6 text-dark">
          Select Branch
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="p-3" style={{ minHeight: '300px' }}>
          <div className="d-flex flex-column gap-2">
            {branches.map((branch) => {
              const active = isSelected(branch.id)
              return (
                <div
                  key={branch.id}
                  className="d-flex align-items-center gap-3 p-2 rounded-3"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: active ? '#fff8e1' : '#fff',
                    border: active ? '1px solid #F89D1D' : '1px solid #e9ecef',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleCheck(branch)}
                >
                  <Form.Check
                    type="checkbox"
                    checked={active}
                    readOnly
                    style={{ pointerEvents: 'none', transform: 'scale(1)' }}
                  />
                  <span
                    className={`fw-semibold ${
                      active ? 'text-dark' : 'text-secondary'
                    }`}
                    style={{ fontSize: '13px' }}
                  >
                    {branch.area_name}
                  </span>
                </div>
              )
            })}
            {branches.length === 0 && (
              <div className="text-center text-muted py-5 mt-3">
                <small>No branches found</small>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 py-3 pe-4">
        <Button
          variant="light"
          onClick={onHide}
          className="rounded px-4 py-2 me-2 fw-semibold text-secondary"
          style={{ fontSize: '13px' }}
        >
          Cancel
        </Button>
        <Button
          className="btn-primary-orange rounded px-4 py-2 fw-semibold"
          onClick={() => onSave(checked)}
          style={{ fontSize: '13px' }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function ModalSelectWarehouse({ show, onHide, onSave, selected, branchIds }) {
  const [warehouses, setWarehouses] = useState([])
  const [checked, setChecked] = useState([])

  useEffect(() => {
    if (show) {
      setChecked(selected)
    }
  }, [show, selected])

  useEffect(() => {
    if (!show) return
    const controller = new AbortController()
    const fetch = async () => {
      try {
        const locs = branchIds.join(',')
        const res = await api.get(
          `master/warehouses/all?is_parent=true&location_id=${locs}`,
          { signal: controller.signal }
        )
        setWarehouses(res.data.data)
      } catch (err) {
        console.error(err)
      }
    }
    fetch()
    return () => controller.abort()
  }, [show, branchIds])

  const handleCheck = (wh) => {
    const exists = checked.find((w) => w.id === wh.id)
    if (exists) {
      setChecked(checked.filter((w) => w.id !== wh.id))
    } else {
      setChecked([...checked, wh])
    }
  }

  const isSelected = (id) => !!checked.find((w) => w.id === id)

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      contentClassName="border-0 rounded-4 shadow-lg"
      scrollable
    >
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="fw-bold fs-6 text-dark">
          Select Warehouse
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="p-3" style={{ minHeight: '300px' }}>
          <div className="d-flex flex-column gap-2">
            {warehouses.map((wh) => {
              const active = isSelected(wh.id)
              return (
                <div
                  key={wh.id}
                  className="d-flex align-items-center gap-3 p-2 rounded-3"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: active ? '#fff8e1' : '#fff',
                    border: active ? '1px solid #F89D1D' : '1px solid #e9ecef',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleCheck(wh)}
                >
                  <Form.Check
                    type="checkbox"
                    checked={active}
                    readOnly
                    style={{ pointerEvents: 'none', transform: 'scale(1)' }}
                  />
                  <span
                    className={`fw-semibold ${
                      active ? 'text-dark' : 'text-secondary'
                    }`}
                    style={{ fontSize: '13px' }}
                  >
                    {wh.warehouse_name}
                  </span>
                </div>
              )
            })}
            {warehouses.length === 0 && (
              <div className="text-center text-muted py-5 mt-3">
                <small>No warehouses found</small>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 py-3 pe-4">
        <Button
          variant="light"
          onClick={onHide}
          className="rounded px-4 py-2 me-2 fw-semibold text-secondary"
          style={{ fontSize: '13px' }}
        >
          Cancel
        </Button>
        <Button
          className="btn-primary-orange rounded px-4 py-2 fw-semibold"
          onClick={() => onSave(checked)}
          style={{ fontSize: '13px' }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function ModalConfirmCancel({ show, onHide, onConfirm }) {
  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Body className="text-center p-4">
        <h5 className="mb-3">Are you sure?</h5>
        <p className="text-muted mb-4">
          You have unsaved changes. Do you really want to cancel?
        </p>
        <div className="d-flex justify-content-center gap-2">
          <Button variant="light border" onClick={onHide}>
            No, Keep Editing
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            Yes, Cancel
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default EditManageRolePage
